import { initializeApp, getApps, getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { apiPost, apiFetch } from './api';

const firebaseConfig = {
  apiKey: "AIzaSyAPijxdkKvPy5FxraPaWzSwqScDHafgAro",
  authDomain: "notescrafter-d3be8.firebaseapp.com",
  projectId: "notescrafter-d3be8",
  storageBucket: "notescrafter-d3be8.firebasestorage.app",
  messagingSenderId: "958189564512",
  appId: "1:958189564512:web:66a90228a526bfef100905",
  measurementId: "G-NS87F2JYB9"
};

const VAPID_KEY = "BF0GzUI_5kwgHTMMX9jKKFphX60a5IacxapowCMcSDeyJmXVDeVvlxahpGL_tl4mC6AF74u4UlOktxAFI9VG4lE";

// Guard: only initialize if not already done
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Initialize Firebase Cloud Messaging
let messaging: any = null;
try {
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    messaging = getMessaging(app);
    console.log('[FCM] Messaging initialized OK');

    // ── Foreground message handler ────────────────────────────────────────────
    // When the app tab is open/focused, FCM delivers to onMessage() instead of
    // the service worker. We manually show a Notification so it's never dropped.
    onMessage(messaging, (payload) => {
      console.log('[FCM] Foreground message received:', payload);
      const title = payload.notification?.title || 'NotesCrafter';
      const body  = payload.notification?.body  || '';
      if (Notification.permission === 'granted') {
        // Use the service worker registration to show the notification so it
        // works even when the page is focused (avoids Chrome's silent-drop rule)
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification(title, {
            body,
            icon: '/logo-192x192.png',
            badge: '/favicon-32x32.png',
            data: payload.data,
            ...((payload.notification as any)?.image ? { image: (payload.notification as any).image } : {}),
          } as NotificationOptions & { image?: string });
        });
      }
    });
  } else {
    console.warn('[FCM] serviceWorker not supported in this environment');
  }
} catch (err) {
  console.error('[FCM] getMessaging() failed:', err);
}

/**
 * Request permission and register FCM device token with the backend
 */
export async function registerFCMToken(): Promise<string | null> {
  console.log('[FCM] registerFCMToken() called');

  if (!messaging) {
    console.warn('[FCM] messaging is null — skipping registration');
    return null;
  }

  // Step 1: Check Notification API support
  if (!('Notification' in window)) {
    console.warn('[FCM] Notification API not supported in this browser');
    return null;
  }

  // Step 2: Request permission
  console.log('[FCM] Step 2: Requesting notification permission...');
  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch (err) {
    console.error('[FCM] requestPermission() threw:', err);
    return null;
  }
  console.log('[FCM] Permission result:', permission);

  if (permission !== 'granted') {
    console.warn('[FCM] Permission not granted:', permission);
    return null;
  }

  // Step 3: Register service worker
  console.log('[FCM] Step 3: Registering service worker /firebase-messaging-sw.js...');
  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/'
    });
    console.log('[FCM] Service worker registered. State:', registration.active?.state ?? 'installing/waiting');
    // Wait for SW to be active
    await navigator.serviceWorker.ready;
    console.log('[FCM] Service worker is ready');
  } catch (err) {
    console.error('[FCM] serviceWorker.register() failed:', err);
    return null;
  }

  // Step 4: Get FCM token
  console.log('[FCM] Step 4: Calling getToken()...');
  let token: string;
  try {
    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });
  } catch (err) {
    console.error('[FCM] getToken() failed:', err);
    return null;
  }

  if (!token) {
    console.warn('[FCM] getToken() returned empty string — no token available');
    return null;
  }

  console.log('[FCM] Step 4 OK — token:', token.slice(0, 20) + '...');

  // Step 5: Register token with backend
  console.log('[FCM] Step 5: Sending token to backend /api/notifications/register...');
  try {
    const res = await apiPost<{ success: boolean }>('/api/notifications/register', {
      fcm_token: token,
      platform: 'web'
    });
    if (res.success) {
      console.log('[FCM] Step 5 OK — token saved to backend');
      localStorage.setItem('nc_fcm_token', token);
    } else {
      console.error('[FCM] Backend returned success:false');
    }
    return token;
  } catch (err) {
    console.error('[FCM] apiPost to /api/notifications/register failed:', err);
    return null;
  }
}

/**
 * Remove registration on logout
 */
export async function unregisterFCMToken(): Promise<void> {
  const token = localStorage.getItem('nc_fcm_token');
  if (!token) return;

  try {
    await apiFetch('/api/notifications/register', {
      method: 'DELETE',
      body: JSON.stringify({ fcm_token: token })
    });
  } catch (err) {
    console.error('[FCM] Error deleting token during logout:', err);
  } finally {
    localStorage.removeItem('nc_fcm_token');
  }
}
